const dayMap = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

/**
 * Generates a cron expression based on schedule payload
 * @param {Object} payload - The schedule payload
 * @returns {string} - The cron expression (e.g. "30 14 * * 1")
 */
// export function generateCronExpression(payload) {
//   if (!payload || !payload.frequency || !payload.scheduleTime) {
//     console.warn("Missing required schedule fields");
//     return "";
//   }

//   const { frequency, scheduleTime, scheduleDay, scheduleMonthDay } = payload;
//   const [hour, minute] = scheduleTime.split(":").map(Number);

//   let cron = "";

//   switch (frequency) {
//     // ⏰ Daily — only time matters
//     case "Daily":
//       cron = `${minute} ${hour} * * *`;
//       break;

//     // 📅 Weekly — time + weekday
//     case "Weekly": {
//       const dayOfWeek = dayMap[scheduleDay] ?? "*";
//       cron = `${minute} ${hour} * * ${dayOfWeek}`;
//       break;
//     }

//     // 🗓️ Monthly — time + (day of month OR weekday)
//     case "Monthly": {
//       // If user selected a specific date like "1" or "15"
//       if (scheduleMonthDay && !isNaN(scheduleMonthDay)) {
//         cron = `${minute} ${hour} ${scheduleMonthDay} * *`;
//       }
//       // Otherwise, if weekday (like "Monday") is selected
//       else if (scheduleDay && dayMap.hasOwnProperty(scheduleDay)) {
//         const dayOfWeek = dayMap[scheduleDay];
//         cron = `${minute} ${hour} * * ${dayOfWeek}`;
//       } else {
//         cron = `${minute} ${hour} 1 * *`; // default: 1st of every month
//       }
//       break;
//     }

//     default:
//       cron = "";
//   }

//   return cron;
// }


export function generateCronExpression(payload) {
  if (!payload || !payload.frequency || !payload.scheduleTime) {
    console.warn("Missing required schedule fields");
    return "0 0 * * *"; // default rontab
  }

  const { frequency, scheduleTime, scheduleDay, scheduleMonthDay } = payload;
  const [hour, minute] = scheduleTime.split(":").map(Number);

  let cron = "0 0 * * *"; // default

  switch (frequency) {
    // ⏰ Daily — only time matters
    case "Daily":
      cron = `${minute} ${hour} * * *`;
      break;

    // 📅 Weekly — time + weekday
    case "Weekly": {
      let dayOfWeek = "*";
      if (scheduleDay && scheduleDay !== "Every Day Of Week") {
        dayOfWeek = dayMap[scheduleDay] ?? "*";
      }
      cron = `${minute} ${hour} * * ${dayOfWeek}`;
      break;
    }

    // 🗓️ Monthly — time + (day of month OR weekday)
    case "Monthly": {
      // Handle "Every Day Of Month"
      if (scheduleMonthDay === "Every Day Of Month") {
        cron = `${minute} ${hour} * * *`;
      }
      // If user selected a specific numeric day like "1" or "15"
      else if (scheduleMonthDay && !isNaN(scheduleMonthDay)) {
        cron = `${minute} ${hour} ${scheduleMonthDay} * *`;
      }
      // If weekday like "Monday" is selected (and not "Every Day Of Week")
      else if (scheduleDay && scheduleDay !== "Every Day Of Week" && dayMap.hasOwnProperty(scheduleDay)) {
        const dayOfWeek = dayMap[scheduleDay];
        cron = `${minute} ${hour} * * ${dayOfWeek}`;
      } else {
        cron = `${minute} ${hour} 1 * *`; // default: 1st of every month
      }
      break;
    }

    default:
      cron = "0 0 * * *";
  }

  return cron;
}



export function formatTimezone(timezoneString) {
  if (!timezoneString) return "UTC";

  // Case 1: If parentheses exist → use the value inside (IANA zone)
  const match = timezoneString.match(/\(([^)]+)\)/);
  if (match && match[1]) {
    return match[1].trim(); // e.g. "Europe/London"
  }

  // Case 2: Extract GMT offset
  const gmtMatch = timezoneString.match(/GMT([+-]\d{2}):?(\d{2})?/);
  if (gmtMatch) {
    const offsetHours = parseInt(gmtMatch[1], 10); // e.g. +05
    const offsetMinutes = parseInt(gmtMatch[2] || "0", 10); // e.g. 30 if "GMT+05:30"

    // Create a DateTimeFormat object with all timezones
    const timeZones = Intl.supportedValuesOf("timeZone");

    // Convert offset to minutes
    const targetOffset = offsetHours * 60 + (offsetHours < 0 ? -offsetMinutes : offsetMinutes);

    // Find the best match by comparing each timezone’s current offset
    const now = new Date();
    for (const zone of timeZones) {
      const tzOffset = -new Date(now.toLocaleString("en-US", { timeZone: zone })).getTimezoneOffset();
      if (tzOffset === targetOffset) {
        return zone;
      }
    }
  }

  // Case 3: If already looks like a valid IANA timezone
  if (/^[A-Za-z]+\/[A-Za-z_]+$/.test(timezoneString)) {
    return timezoneString;
  }

  // Default fallback
  return "UTC";
}

export function parseCronPattern(cron) {
  if (!cron) return {};

  //eslint-disable-next-line
  const [minute, hour, dayOfMonth, month, dayOfWeek] = cron.split(" ");

  // Format time → HH:mm (with leading zeros)
  const time = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;

  // Identify frequency
  let frequency = "Custom";
  let scheduleDay = null;
  let scheduleMonthDay = null;

  // Case 1: Daily → * * * * *
  if (dayOfMonth === "*" && dayOfWeek === "*") {
    frequency = "Daily";
  }

  // Case 2: Weekly → dayOfWeek = 0–6 (0 = Sunday)
  else if (dayOfMonth === "*" && dayOfWeek !== "*") {
    frequency = "Weekly";
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    scheduleDay = days[parseInt(dayOfWeek, 10)];
  }

  // Case 3: Monthly → specific dayOfMonth number
  else if (dayOfMonth !== "*" && dayOfWeek === "*") {
    frequency = "Monthly";
    scheduleMonthDay = dayOfMonth;
  }

  // Case 4: Both dayOfMonth and dayOfWeek set → rare custom
  else {
    frequency = "Custom";
  }

  return {
    frequency,
    scheduleTime: time,
    scheduleDay,
    scheduleMonthDay,
  };
}


export function getEmailFields(recipients) {
  if (!Array.isArray(recipients) || recipients.length === 0) {
    return { emailAddress: "", cc: [], bcc: [] };
  }

  const emailRecipient = recipients.find((r) => r.type === "Email");
  if (!emailRecipient) {
    return { emailAddress: "", cc: [], bcc: [] };
  }

  let config = {};
  try {
    config = JSON.parse(emailRecipient.recipient_config_json || "{}");
  } catch {
    config = {};
  }

  // Utility to safely split comma-separated emails into array
  const parseEmails = (str) => {
    if (!str || typeof str !== "string") return [];
    return str
      .split(",")                // split by comma
      .map((s) => s.trim())      // remove extra spaces
      .filter((s) => s !== "");  // remove empty strings
  };

  return {
    emailAddress: config.target || "",
    cc: parseEmails(config.ccTarget),
    bcc: parseEmails(config.bccTarget),
  };
}